import React, { useState, useEffect } from 'react';
import { Banner, Button, InlineStack, Text, BlockStack } from '@shopify/polaris';
import { AlertTriangleIcon } from '@shopify/polaris-icons';
import axios from 'axios';

interface CreditAlertProps {}

const CreditAlert: React.FC<CreditAlertProps> = () => {
  const [credits, setCredits] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [supportPhone, setSupportPhone] = useState<string>('');

  useEffect(() => {
    fetchCredits();
    fetchSupportPhone();
  }, []);

  const fetchCredits = async () => {
    try {
      const response = await axios.get('/ai_settings');
      if (response.data?.success) {
        setCredits(response.data.data?.tenant?.available_credits || 0);
      }
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSupportPhone = async () => {
    try {
      const response = await axios.get('/ai_settings');
      if (response.data?.success) {
        setSupportPhone(response.data.data?.support_phone || '');
      }
    } catch (error) {
      console.error('Error fetching support phone:', error);
    }
  };

  // Don't show if loading or credits are sufficient
  if (loading || credits === null || credits >= 0.5) {
    return null;
  }

  const isCritical = credits <= 0;
  const tone = isCritical ? 'critical' : 'warning';

  const handleContactSupport = () => {
    if (supportPhone) {
      // Sanitize phone: only allow digits, spaces, hyphens, parentheses, plus
      const sanitized = supportPhone.replace(/[^\d\s\-()+]/g, '');
      if (sanitized) {
        window.location.href = `tel:${encodeURIComponent(sanitized)}`;
      } else {
        window.open('/support', '_blank', 'noopener,noreferrer');
      }
    } else {
      window.open('/support', '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <Banner
        tone={tone}
        icon={AlertTriangleIcon}
        title={isCritical ? 'No Credits Available' : 'Low Credit Balance'}
      >
        <BlockStack gap="300">
          <Text as="p">
            {isCritical 
              ? 'You have no credits remaining. AI analysis is disabled until credits are added.'
              : `You have ${credits} credits remaining. Consider adding more credits to continue using AI services.`
            }
          </Text>
          <InlineStack gap="200" align="start">
            <Button
              size="slim"
              tone={isCritical ? 'critical' : undefined}
              onClick={handleContactSupport}
            >
              {supportPhone ? `Call Support (${supportPhone})` : 'Contact Support'}
            </Button>
          </InlineStack>
        </BlockStack>
      </Banner>
    </div>
  );
};

export default CreditAlert;
